// ═══════════════════════════════════════════════════════════════════════════
// AuditTab — read-only view of the admin audit log (migration 071). Shows who
// did what, most recent first. Visible to any CMS user (admins + managers).
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { listAuditLog } from '../../lib/audit';

const LABELS = {
  feature_episode:   'Featured episode',
  unfeature_episode: 'Removed feature',
  update_feature:    'Edited feature',
  grant_credits:     'Granted credits',
  set_credits:       'Set credits',
  update_user:       'Updated user',
};

function timeAgo(iso) {
  const d = new Date(iso).getTime();
  if (!d) return '';
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listAuditLog(150);
        if (alive) setEntries(rows);
      } catch (e) {
        if (alive) setError(e?.message || 'Could not load the audit log.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="mkt-empty">Loading…</div>;
  if (error) return <div className="mkt-empty">{error}</div>;
  if (!entries.length) return <div className="mkt-empty">No admin actions logged yet.</div>;

  return (
    <div>
      <h2 className="mkt-panel-title">Audit log</h2>
      <p className="mkt-panel-sub">Every admin action, most recent first. Read-only.</p>
      <div className="audit-list">
        {entries.map((e) => {
          const summary =
            e.detail && typeof e.detail === 'object' && Object.keys(e.detail).length
              ? Object.entries(e.detail).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')
              : '';
          return (
            <div key={e.id} className="audit-row">
              <span className="audit-action">{LABELS[e.action] || e.action}</span>
              <span className="audit-actor">{e.actor_email || 'unknown'}</span>
              {e.target_type && <span className="audit-target">{e.target_type} {e.target_id ? `· ${String(e.target_id).slice(0, 8)}` : ''}</span>}
              {summary && <span className="audit-detail" title={summary}>{summary}</span>}
              <span className="audit-time">{timeAgo(e.created_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
